import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertCircle, ArrowDown, ArrowUp, FileText, Info, Loader2, Plus, RotateCcw, Trash2, Upload } from 'lucide-react';
import {
  cleanFormAnswers,
  describeFormCondition,
  editableNumber,
  evaluateForm,
  formatAnswer,
  formId,
  parseDecimal,
  rowKey,
  validateForm,
  type FormAnswers,
  type FormEvaluation,
  type FormIssue,
  type FormItem,
  type FormPublicDefinition,
  type FormQuestion,
  type FormRowValue,
  type FormSection,
  type FormUploadInfo,
  type FormValue,
} from '@kaba/shared';
import { Button, cx } from '../ui/ui';

export const fieldCls = 'min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base sm:text-sm';

export interface UploadApi {
  list: FormUploadInfo[];
  /** Liefert die neue Datei; nur sie wird in die Antwort übernommen (F04) */
  upload: (key: string, file: File, onProgress: (p: number) => void) => Promise<FormUploadInfo>;
  remove: (id: string) => Promise<void>;
  url: (id: string, thumb?: boolean) => string | null;
  /** Vorschau: keine echten Uploads */
  disabled?: string;
}

type Step = { kind: 'page'; id: string } | { kind: 'summary' };

/**
 * Formular ausfüllen mit realer Logik (R03–R07): Seiten überspringen, Pflicht nur sichtbar,
 * Fehler am Feld und als verlinkte Übersicht, Zusammenfassung vor dem Absenden.
 */
export function FormRenderer({
  def,
  answers,
  onChange,
  uploads,
  onSubmit,
  submitting,
  serverIssues,
  preview,
  submitLabel = 'Absenden',
}: {
  def: FormPublicDefinition;
  answers: FormAnswers;
  onChange: (a: FormAnswers, removed: string[]) => void;
  uploads: UploadApi;
  onSubmit: () => Promise<void> | void;
  submitting?: boolean;
  serverIssues?: FormIssue[];
  /** Vorschau: zeigt „Warum sichtbar?“ */
  preview?: boolean;
  submitLabel?: string;
}) {
  const ev = useMemo(() => evaluateForm(def, answers), [def, answers]);
  const steps: Step[] = useMemo(() => [...ev.pages.map((id) => ({ kind: 'page' as const, id })), { kind: 'summary' as const }], [ev.pages]);
  const [stepId, setStepId] = useState<string>(ev.pages[0] ?? 'summary');
  const [shownIssues, setShownIssues] = useState<FormIssue[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  // Fällt die aktuelle Seite weg (frühere Antwort geändert), zur nächsten sichtbaren springen (R04)
  const idx = Math.max(
    0,
    steps.findIndex((s) => (s.kind === 'page' ? s.id === stepId : stepId === 'summary')),
  );
  const step = steps[idx]!;
  useEffect(() => {
    if (step.kind === 'page' && step.id !== stepId) setStepId(step.id);
  }, [step, stepId]);

  const change = (next: FormAnswers) => {
    const e = evaluateForm(def, next);
    const cleaned = cleanFormAnswers(def, e);
    if (e.removed.length) setNotice('Nicht mehr benötigte Antworten wurden entfernt.');
    onChange(cleaned, e.removed);
    if (shownIssues) setShownIssues(validateForm(def, e, { pageId: step.kind === 'page' ? step.id : undefined, uploads: uploads.list }));
  };
  const setValue = (key: string, v: FormValue) => {
    const next = { ...answers };
    if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) delete next[key];
    else next[key] = v;
    change(next);
  };
  const setRows = (sectionId: string, rows: FormRowValue[]) => change({ ...answers, [sectionId]: rows });

  const issues = [...(shownIssues ?? []), ...(serverIssues ?? []).filter((s) => !(shownIssues ?? []).some((x) => x.key === s.key))];
  const issueOf = (key: string) => issues.find((i) => i.key === key)?.message;

  const go = (to: number) => {
    setShownIssues(null);
    setNotice(null);
    const s = steps[to]!;
    setStepId(s.kind === 'page' ? s.id : 'summary');
    requestAnimationFrame(() => topRef.current?.focus());
  };
  const next = () => {
    if (step.kind !== 'page') return;
    const found = validateForm(def, ev, { pageId: step.id, uploads: uploads.list });
    if (found.length) {
      setShownIssues(found);
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }
    go(idx + 1);
  };
  const submit = async () => {
    const found = validateForm(def, ev, { uploads: uploads.list });
    if (found.length) {
      // Zur ersten Seite mit Fehler
      const page = def.pages.find((p) => found.some((f) => pageOfKey(p.items, f.key)));
      if (page) setStepId(page.id);
      setShownIssues(found);
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }
    await onSubmit();
  };
  useEffect(() => {
    if (serverIssues?.length) requestAnimationFrame(() => summaryRef.current?.focus());
  }, [serverIssues]);

  const page = step.kind === 'page' ? def.pages.find((p) => p.id === step.id) : null;
  const visibleSteps = steps.length;
  const ctx: Ctx = { def, ev, answers, setValue, setRows, issueOf, uploads, preview: !!preview };

  return (
    <div className="flex flex-col gap-5">
      <div ref={topRef} tabIndex={-1} className="flex flex-col gap-1 outline-none">
        <p className="text-xs font-medium text-[var(--ink-soft)]" aria-live="polite">
          {visibleSteps > 1 && `Schritt ${idx + 1} von ${visibleSteps}`}
        </p>
        {visibleSteps > 1 && (
          <div className="h-1.5 overflow-hidden rounded-full bg-black/5 dark:bg-white/10" aria-hidden>
            <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${((idx + 1) / visibleSteps) * 100}%` }} />
          </div>
        )}
        <h2 className="mt-2 text-xl font-semibold">{page ? page.title || def.title : 'Zusammenfassung'}</h2>
      </div>

      {notice && (
        <p role="status" className="flex items-center gap-2 rounded-lg bg-sky-50 p-2.5 text-sm text-sky-900 dark:bg-sky-950 dark:text-sky-100">
          <Info size={16} aria-hidden /> {notice}
        </p>
      )}

      {issues.length > 0 && (
        <div ref={summaryRef} tabIndex={-1} role="alert" className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 outline-none dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100">
          <p className="mb-1 flex items-center gap-1.5 font-semibold">
            <AlertCircle size={16} aria-hidden /> Bitte prüfe {issues.length === 1 ? 'diese Angabe' : `diese ${issues.length} Angaben`}:
          </p>
          <ul className="list-disc pl-5">
            {issues.map((i) => (
              <li key={i.key + i.message}>
                <a
                  href={`#ff-${cssKey(i.key)}`}
                  className="underline"
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(`ff-${cssKey(i.key)}`);
                    el?.scrollIntoView({ block: 'center' });
                    (el?.querySelector('input,select,textarea,button') as HTMLElement | null)?.focus();
                  }}
                >
                  {labelOfKey(def, i.key)}: {i.message}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {page && preview && ev.because[page.id] && (
        <details className="-mt-3 text-xs text-[var(--ink-soft)]">
          <summary className="cursor-pointer">Warum sichtbar?</summary>
          <p className="mt-1">Weil {describeFormCondition(def, ev.because[page.id])}.</p>
        </details>
      )}
      {page && (
        <div className="flex flex-col gap-5">
          {page.items.map((it) => (
            <ItemView key={it.id} item={it} ctx={ctx} />
          ))}
        </div>
      )}

      {step.kind === 'summary' && <Summary def={def} ev={ev} uploads={uploads.list} onEdit={(pid) => setStepId(pid)} />}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--line)] pt-4">
        {idx > 0 ? (
          <Button type="button" onClick={() => go(idx - 1)}>
            Zurück
          </Button>
        ) : (
          <span />
        )}
        {step.kind === 'page' ? (
          <Button type="button" variant="primary" onClick={next}>
            Weiter
          </Button>
        ) : (
          <Button type="button" variant="primary" onClick={() => void submit()} disabled={submitting}>
            {submitting && <Loader2 size={16} className="animate-spin" aria-hidden />}
            {submitLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

const cssKey = (k: string) => k.replace(/[^\w-]/g, '_');

function pageOfKey(items: FormItem[], key: string): boolean {
  const base = key.includes('[') ? key.split('[')[0]! : key;
  return items.some((it) => it.id === base || (it.kind === 'section' && it.items.some((q) => q.id === base)));
}

function labelOfKey(def: FormPublicDefinition, key: string): string {
  const m = /^([\w-]+)\[([\w-]+)\]\.([\w-]+)$/.exec(key);
  for (const p of def.pages)
    for (const it of p.items) {
      if (m && it.kind === 'section' && it.id === m[1]) return `${it.title}: ${it.items.find((q) => q.id === m[3])?.label ?? ''}`;
      if (it.id === key) return it.kind === 'section' ? it.title : it.label;
      if (it.kind === 'section') {
        const q = it.items.find((x) => x.id === key);
        if (q) return q.label;
      }
    }
  return key;
}

interface Ctx {
  def: FormPublicDefinition;
  ev: FormEvaluation;
  answers: FormAnswers;
  setValue: (key: string, v: FormValue) => void;
  setRows: (sectionId: string, rows: FormRowValue[]) => void;
  issueOf: (key: string) => string | undefined;
  uploads: UploadApi;
  preview: boolean;
}

function Why({ ctx, id }: { ctx: Ctx; id: string }) {
  const c = ctx.ev.because[id];
  if (!ctx.preview || !c) return null;
  return (
    <details className="text-xs text-[var(--ink-soft)]">
      <summary className="cursor-pointer">Warum sichtbar?</summary>
      <p className="mt-1">Weil {describeFormCondition(ctx.def, c)}.</p>
    </details>
  );
}

function ItemView({ item, ctx }: { item: FormItem; ctx: Ctx }) {
  if (item.kind === 'question') {
    if (!ctx.ev.visible.has(item.id)) return null;
    return <QuestionView q={item} k={item.id} value={ctx.answers[item.id]} onValue={(v) => ctx.setValue(item.id, v)} ctx={ctx} />;
  }
  if (!ctx.ev.visible.has(item.id)) return null;
  if (!item.repeat)
    return (
      <fieldset className="flex flex-col gap-4 rounded-xl border border-[var(--line)] p-3 sm:p-4">
        <legend className="px-1 text-base font-semibold">{item.title}</legend>
        {item.help && <p className="-mt-2 text-sm text-[var(--ink-soft)]">{item.help}</p>}
        <Why ctx={ctx} id={item.id} />
        {item.items.map((q) =>
          ctx.ev.visible.has(q.id) ? <QuestionView key={q.id} q={q} k={q.id} value={ctx.answers[q.id]} onValue={(v) => ctx.setValue(q.id, v)} ctx={ctx} /> : null,
        )}
      </fieldset>
    );
  return <RepeatView sec={item} ctx={ctx} />;
}

function RepeatView({ sec, ctx }: { sec: FormSection; ctx: Ctx }) {
  const rows = (Array.isArray(ctx.answers[sec.id]) ? ctx.answers[sec.id] : []) as FormRowValue[];
  const r = sec.repeat!;
  const err = ctx.issueOf(sec.id);
  // Mindestanzahl gleich als leere Einträge anbieten
  useEffect(() => {
    if (!rows.length && r.min > 0) ctx.setRows(sec.id, Array.from({ length: r.min }, () => ({ _id: formId('r') })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);
  const setRow = (i: number, row: FormRowValue) => ctx.setRows(sec.id, rows.map((x, j) => (j === i ? row : x)));
  const move = (i: number, d: number) => {
    const n = [...rows];
    const [x] = n.splice(i, 1);
    n.splice(i + d, 0, x!);
    ctx.setRows(sec.id, n);
  };
  return (
    <fieldset id={`ff-${cssKey(sec.id)}`} className="flex flex-col gap-3">
      <legend className="text-base font-semibold">{sec.title}</legend>
      {sec.help && <p className="text-sm text-[var(--ink-soft)]">{sec.help}</p>}
      <Why ctx={ctx} id={sec.id} />
      {err && <p className="text-sm text-rose-700 dark:text-rose-300">{err}</p>}
      {rows.map((row, i) => (
        <section key={row._id} aria-label={`${r.itemLabel || 'Eintrag'} ${i + 1}`} className="flex flex-col gap-3 rounded-xl border border-[var(--line)] p-3">
          <div className="flex items-center gap-1">
            <h4 className="flex-1 text-sm font-semibold">
              {r.itemLabel || 'Eintrag'} {i + 1}
            </h4>
            <button type="button" className="inline-flex size-10 items-center justify-center rounded-md hover:bg-black/5 disabled:opacity-30 dark:hover:bg-white/10" aria-label={`${r.itemLabel} ${i + 1} nach oben`} disabled={i === 0} onClick={() => move(i, -1)}>
              <ArrowUp size={16} />
            </button>
            <button type="button" className="inline-flex size-10 items-center justify-center rounded-md hover:bg-black/5 disabled:opacity-30 dark:hover:bg-white/10" aria-label={`${r.itemLabel} ${i + 1} nach unten`} disabled={i === rows.length - 1} onClick={() => move(i, 1)}>
              <ArrowDown size={16} />
            </button>
            <button type="button" className="inline-flex size-10 items-center justify-center rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950" aria-label={`${r.itemLabel} ${i + 1} entfernen`} onClick={() => ctx.setRows(sec.id, rows.filter((_, j) => j !== i))}>
              <Trash2 size={16} />
            </button>
          </div>
          {sec.items.map((q) => {
            const key = rowKey(sec.id, row._id, q.id);
            if (!ctx.ev.visible.has(key)) return null;
            return (
              <QuestionView
                key={q.id}
                q={q}
                k={key}
                value={q.type === 'calc' ? undefined : row[q.id]}
                onValue={(v) => {
                  const nr = { ...row };
                  if (v === null || v === '' || (Array.isArray(v) && !v.length)) delete nr[q.id];
                  else nr[q.id] = v;
                  setRow(i, nr);
                }}
                ctx={ctx}
              />
            );
          })}
        </section>
      ))}
      {rows.length < r.max && (
        <Button type="button" className="self-start" onClick={() => ctx.setRows(sec.id, [...rows, { _id: formId('r') }])}>
          <Plus size={16} /> {r.itemLabel || 'Eintrag'} hinzufügen
        </Button>
      )}
    </fieldset>
  );
}

function QuestionView({ q, k, value, onValue, ctx }: { q: FormQuestion; k: string; value: FormValue | undefined; onValue: (v: FormValue) => void; ctx: Ctx }) {
  const id = useId();
  const err = ctx.issueOf(k);
  const req = ctx.ev.required.has(k);
  const help = q.help ? `${id}-h` : undefined;
  const errId = err ? `${id}-e` : undefined;
  const describedBy = [help, errId].filter(Boolean).join(' ') || undefined;
  if (q.type === 'info')
    return (
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold">{q.label}</h3>
        {q.help && <p className="text-sm whitespace-pre-line text-[var(--ink-soft)]">{q.help}</p>}
        <Why ctx={ctx} id={k} />
      </div>
    );
  const label = (
    <>
      {q.label}
      {req ? <span className="text-xs font-normal text-[var(--ink-soft)]"> (Pflicht)</span> : null}
    </>
  );
  const inputProps = { id, 'aria-describedby': describedBy, 'aria-invalid': !!err || undefined, 'aria-required': req || undefined };
  let control: ReactNode;
  let group = false;
  switch (q.type) {
    case 'text':
    case 'email':
    case 'phone':
    case 'url':
      control = (
        <input
          {...inputProps}
          className={fieldCls}
          type={q.type === 'email' ? 'email' : q.type === 'phone' ? 'tel' : q.type === 'url' ? 'url' : 'text'}
          inputMode={q.type === 'email' ? 'email' : q.type === 'phone' ? 'tel' : q.type === 'url' ? 'url' : undefined}
          autoComplete={q.type === 'email' ? 'email' : q.type === 'phone' ? 'tel' : undefined}
          maxLength={q.maxLength ?? 300}
          placeholder={q.placeholder}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onValue(e.target.value)}
        />
      );
      break;
    case 'longtext':
      control = (
        <textarea
          {...inputProps}
          className={cx(fieldCls, 'min-h-28 py-2')}
          maxLength={q.maxLength ?? 5000}
          placeholder={q.placeholder}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onValue(e.target.value)}
        />
      );
      break;
    case 'number':
    case 'currency':
      control = <NumberInput inputProps={inputProps} value={typeof value === 'number' ? value : null} currency={q.type === 'currency'} unit={q.type === 'currency' ? '€' : q.unit} onValue={onValue} />;
      break;
    case 'date':
      control = <input {...inputProps} className={fieldCls} type="date" value={typeof value === 'string' ? value : ''} onChange={(e) => onValue(e.target.value)} />;
      break;
    case 'datetime':
      control = (
        <div className="flex flex-col gap-1">
          <input
            {...inputProps}
            className={fieldCls}
            type="datetime-local"
            value={typeof value === 'string' && value ? toLocalInput(value) : ''}
            onChange={(e) => onValue(e.target.value ? new Date(e.target.value).toISOString() : null)}
          />
          <span className="text-xs text-[var(--ink-soft)]">Zeitzone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</span>
        </div>
      );
      break;
    case 'choice': {
      const opts = (q.options ?? []).filter((o) => (ctx.ev.options[k] ?? []).includes(o.id));
      const asSelect = q.display === 'select' || (q.display !== 'radio' && opts.length > 6);
      if (asSelect)
        control = (
          <select {...inputProps} className={fieldCls} value={typeof value === 'string' ? value : ''} onChange={(e) => onValue(e.target.value || null)}>
            <option value="">Bitte wählen …</option>
            {opts.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        );
      else {
        group = true;
        control = (
          <div className="flex flex-col gap-1">
            {opts.map((o) => (
              <label key={o.id} className="flex min-h-11 items-center gap-3 rounded-lg border border-[var(--line)] px-3 text-sm has-[:checked]:border-accent has-[:checked]:bg-accent-soft/40 sm:min-h-10">
                <input type="radio" name={id} className="size-5 accent-[var(--color-accent)]" checked={value === o.id} onChange={() => onValue(o.id)} />
                {o.label}
              </label>
            ))}
          </div>
        );
      }
      break;
    }
    case 'multichoice': {
      group = true;
      const opts = (q.options ?? []).filter((o) => (ctx.ev.options[k] ?? []).includes(o.id));
      const cur = Array.isArray(value) ? (value as string[]) : [];
      control = (
        <div className="flex flex-col gap-1">
          {opts.map((o) => (
            <label key={o.id} className="flex min-h-11 items-center gap-3 rounded-lg border border-[var(--line)] px-3 text-sm has-[:checked]:border-accent has-[:checked]:bg-accent-soft/40 sm:min-h-10">
              <input
                type="checkbox"
                className="size-5 accent-[var(--color-accent)]"
                checked={cur.includes(o.id)}
                onChange={(e) => onValue(e.target.checked ? [...cur, o.id] : cur.filter((x) => x !== o.id))}
              />
              {o.label}
            </label>
          ))}
        </div>
      );
      break;
    }
    case 'yesno':
      group = true;
      control = (
        <div className="flex gap-2">
          {[true, false].map((b) => (
            <label key={String(b)} className="flex min-h-11 flex-1 items-center gap-3 rounded-lg border border-[var(--line)] px-3 text-sm has-[:checked]:border-accent has-[:checked]:bg-accent-soft/40 sm:min-h-10 sm:flex-none sm:pr-6">
              <input type="radio" name={id} className="size-5 accent-[var(--color-accent)]" checked={value === b} onChange={() => onValue(b)} />
              {b ? 'Ja' : 'Nein'}
            </label>
          ))}
        </div>
      );
      break;
    case 'confirm':
      return (
        <div id={`ff-${cssKey(k)}`} className="flex flex-col gap-1">
          <label className="flex min-h-11 items-start gap-3 text-sm">
            <input {...inputProps} type="checkbox" className="mt-0.5 size-5 shrink-0 accent-[var(--color-accent)]" checked={value === true} onChange={(e) => onValue(e.target.checked ? true : null)} />
            <span>{label}</span>
          </label>
          {q.help && (
            <p id={help} className="pl-8 text-sm text-[var(--ink-soft)]">
              {q.help}
            </p>
          )}
          {err && (
            <p id={errId} className="pl-8 text-sm text-rose-700 dark:text-rose-300">
              {err}
            </p>
          )}
          <Why ctx={ctx} id={k} />
        </div>
      );
    case 'rating':
      group = true;
      control = (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <label key={n} className="flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-lg border border-[var(--line)] px-3 text-sm has-[:checked]:border-accent has-[:checked]:bg-accent has-[:checked]:text-on-accent">
                <input type="radio" name={id} className="sr-only" checked={value === n} onChange={() => onValue(n)} />
                {n}
                <span className="sr-only">{n === 1 ? q.lowLabel : n === 5 ? q.highLabel : ''} von 5</span>
              </label>
            ))}
          </div>
          {(q.lowLabel || q.highLabel) && (
            <p className="flex justify-between text-xs text-[var(--ink-soft)]">
              <span>1 = {q.lowLabel}</span>
              <span>5 = {q.highLabel}</span>
            </p>
          )}
        </div>
      );
      break;
    case 'calc': {
      const c = ctx.ev.calc[k];
      control = (
        <output id={id} className="flex min-h-11 items-center rounded-lg bg-[var(--column)] px-3 text-sm font-semibold tabular-nums" aria-live="polite">
          {c?.value != null ? formatAnswer(q, c.value) : c?.error === 'div0' ? 'Nicht berechenbar (Division durch 0)' : 'Noch nicht berechenbar'}
        </output>
      );
      break;
    }
    case 'file':
      group = true;
      control = <FileField q={q} k={k} value={value} onValue={onValue} ctx={ctx} describedBy={describedBy} />;
      break;
  }
  return (
    <div id={`ff-${cssKey(k)}`} className="flex flex-col gap-1.5">
      {group ? (
        <fieldset aria-describedby={describedBy} className="flex flex-col gap-1.5">
          <legend className="mb-1 text-sm font-medium">{label}</legend>
          {q.help && (
            <p id={help} className="-mt-1 text-sm text-[var(--ink-soft)]">
              {q.help}
            </p>
          )}
          {control}
        </fieldset>
      ) : (
        <>
          <label htmlFor={id} className="text-sm font-medium">
            {label}
          </label>
          {q.help && (
            <p id={help} className="-mt-1 text-sm text-[var(--ink-soft)]">
              {q.help}
            </p>
          )}
          {control}
        </>
      )}
      {err && (
        <p id={errId} className="flex items-center gap-1 text-sm text-rose-700 dark:text-rose-300">
          <AlertCircle size={14} aria-hidden /> {err}
        </p>
      )}
      <Why ctx={ctx} id={k} />
    </div>
  );
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function NumberInput({ inputProps, value, currency, unit, onValue }: { inputProps: Record<string, unknown>; value: number | null; currency: boolean; unit?: string; onValue: (v: FormValue) => void }) {
  const [text, setText] = useState(value != null ? editableNumber(value, currency) : '');
  const [bad, setBad] = useState(false);
  useEffect(() => {
    // Von außen geändert (z. B. entfernt): Anzeige angleichen
    const parsed = parseDecimal(text);
    if (value === null && parsed !== null) setText('');
    if (value !== null && parsed !== value) setText(editableNumber(value, currency));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <input
          {...inputProps}
          className={fieldCls}
          inputMode="decimal"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            const n = parseDecimal(e.target.value);
            setBad(!!e.target.value.trim() && n === null);
            onValue(n);
          }}
        />
        {unit && <span className="shrink-0 text-sm text-[var(--ink-soft)]">{unit}</span>}
      </div>
      {bad && <span className="text-xs text-rose-700 dark:text-rose-300">Keine gültige Zahl – z. B. 12 oder 12,50.</span>}
    </div>
  );
}

function FileField({ q, k, value, onValue, ctx, describedBy }: { q: FormQuestion; k: string; value: FormValue | undefined; onValue: (v: FormValue) => void; ctx: Ctx; describedBy?: string }) {
  const ids = Array.isArray(value) ? (value as string[]) : [];
  const files = ids.map((id) => ctx.uploads.list.find((u) => u.id === id)).filter((u): u is FormUploadInfo => !!u);
  const [busy, setBusy] = useState<{ name: string; p: number; error?: string; file: File }[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const max = q.maxFiles ?? 5;
  const accept = (q.accept?.length ? q.accept : ['image', 'pdf', 'text'])
    .map((a) => (a === 'image' ? '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp' : a === 'pdf' ? '.pdf,application/pdf' : '.txt,text/plain'))
    .join(',');
  // Neueste Antwort und Setzer – der Upload endet erst nach späteren Eingaben
  const live = useRef({ ids, onValue });
  live.current = { ids, onValue };
  const start = async (file: File) => {
    setBusy((b) => [...b, { name: file.name, p: 0, file }]);
    try {
      const u = await ctx.uploads.upload(k, file, (p) => setBusy((b) => b.map((x) => (x.file === file ? { ...x, p } : x))));
      setBusy((b) => b.filter((x) => x.file !== file));
      // Nur der eben erfolgreich hochgeladene Upload kommt in die Antwort – nie alte, bewusst entfernte (F04)
      if (u.status === 'ready' && !live.current.ids.includes(u.id)) live.current.onValue([...live.current.ids, u.id]);
    } catch (e) {
      setBusy((b) => b.map((x) => (x.file === file ? { ...x, error: (e as Error).message } : x)));
    }
  };
  // Früher hochgeladen, aber nicht (mehr) Teil der Antwort – z. B. nach Aus- und Einblenden der Frage
  const earlier = ctx.uploads.list.filter((u) => u.key === k && u.status === 'ready' && !ids.includes(u.id));
  return (
    <div className="flex flex-col gap-2" aria-describedby={describedBy}>
      {files.map((f) => {
        const thumb = f.mime.startsWith('image/') ? ctx.uploads.url(f.id, true) : null;
        return (
          <div key={f.id} className="flex items-center gap-3 rounded-lg border border-[var(--line)] p-2 text-sm">
            {thumb ? <img src={thumb} alt="" className="size-12 rounded object-cover" /> : <FileText size={24} className="m-3 text-[var(--ink-soft)]" aria-hidden />}
            <span className="min-w-0 flex-1 truncate">{f.name}</span>
            <span className="text-xs text-[var(--ink-soft)]">{f.status === 'ready' ? `${Math.ceil(f.size / 1024)} KB` : f.status === 'quarantine' ? 'wird geprüft …' : 'abgelehnt'}</span>
            <button
              type="button"
              className="inline-flex size-10 items-center justify-center rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950"
              aria-label={`${f.name} entfernen`}
              onClick={async () => {
                await ctx.uploads.remove(f.id);
                onValue(ids.filter((x) => x !== f.id));
              }}
            >
              <Trash2 size={16} />
            </button>
          </div>
        );
      })}
      {earlier.length > 0 && (
        <div className="flex flex-col gap-1.5 rounded-lg bg-[var(--column)] p-2.5 text-sm">
          <p className="text-xs text-[var(--ink-soft)]">Früher hochgeladen, gehört nicht zur Antwort:</p>
          {earlier.map((f) => (
            <div key={f.id} className="flex flex-wrap items-center gap-2">
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              {files.length + busy.length < max && (
                <button type="button" className="inline-flex min-h-10 items-center gap-1 rounded-md px-2 underline" onClick={() => onValue([...ids, f.id])}>
                  Wieder verwenden
                </button>
              )}
              <button type="button" className="inline-flex size-10 items-center justify-center rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950" aria-label={`${f.name} löschen`} onClick={() => void ctx.uploads.remove(f.id)}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
      {busy.map((b, i) => (
        <div key={i} className="flex items-center gap-3 rounded-lg border border-dashed border-[var(--line)] p-2 text-sm" role="status">
          {b.error ? <AlertCircle size={18} className="text-rose-600" aria-hidden /> : <Loader2 size={18} className="animate-spin" aria-hidden />}
          <span className="min-w-0 flex-1 truncate">
            {b.name} {b.error ? `– ${b.error}` : `– ${Math.round(b.p * 100)} %`}
          </span>
          {b.error && (
            <>
              <button type="button" className="inline-flex min-h-10 items-center gap-1 rounded-md px-2 hover:bg-black/5" onClick={() => {
                setBusy((x) => x.filter((y) => y !== b));
                void start(b.file);
              }}>
                <RotateCcw size={14} /> Wiederholen
              </button>
              <button type="button" className="inline-flex size-10 items-center justify-center rounded-md" aria-label="Verwerfen" onClick={() => setBusy((x) => x.filter((y) => y !== b))}>
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      ))}
      {ctx.uploads.disabled ? (
        <p className="rounded-lg bg-[var(--column)] p-2.5 text-sm text-[var(--ink-soft)]">{ctx.uploads.disabled}</p>
      ) : (
        files.length + busy.length < max && (
          <label
            className="flex min-h-16 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-[var(--line)] p-3 text-center text-sm hover:bg-black/[0.02] dark:hover:bg-white/5"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              for (const f of [...e.dataTransfer.files].slice(0, max - files.length)) void start(f);
            }}
          >
            <input
              ref={inputRef}
              type="file"
              multiple={max > 1}
              accept={accept}
              className="sr-only"
              aria-label={`${q.label}: Datei auswählen`}
              onChange={(e) => {
                for (const f of [...(e.target.files ?? [])].slice(0, max - files.length)) void start(f);
                e.target.value = '';
              }}
            />
            <span className="inline-flex items-center gap-2 font-medium">
              <Upload size={16} aria-hidden /> Datei auswählen
            </span>
            <span className="text-xs text-[var(--ink-soft)]">
              Bis zu {max} {max === 1 ? 'Datei' : 'Dateien'}, je höchstens {q.maxSizeMb ?? 10} MB
            </span>
          </label>
        )
      )}
    </div>
  );
}

/** Zusammenfassung aller aktiven Antworten mit „Bearbeiten“ je Seite (R04) */
export function Summary({ def, ev, uploads, onEdit }: { def: FormPublicDefinition; ev: FormEvaluation; uploads: FormUploadInfo[]; onEdit?: (pageId: string) => void }) {
  const names = new Map(uploads.map((u) => [u.id, { name: u.name }]));
  return (
    <div className="flex flex-col gap-4">
      {def.pages
        .filter((p) => ev.pages.includes(p.id))
        .map((p) => (
          <section key={p.id} aria-label={p.title || 'Angaben'} className="rounded-xl border border-[var(--line)]">
            <div className="flex items-center gap-2 border-b border-[var(--line)] px-3 py-2">
              <h3 className="flex-1 text-sm font-semibold">{p.title || 'Angaben'}</h3>
              {onEdit && (
                <button type="button" className="min-h-10 rounded-md px-2 text-sm text-accent-fg hover:underline" onClick={() => onEdit(p.id)}>
                  Bearbeiten<span className="sr-only">: {p.title}</span>
                </button>
              )}
            </div>
            <dl className="divide-y divide-[var(--line)] text-sm">
              {p.items.flatMap((it) => {
                if (!ev.visible.has(it.id)) return [];
                if (it.kind === 'question') {
                  if (it.type === 'info') return [];
                  const v = it.type === 'calc' ? ev.calc[it.id]?.value : ev.active[it.id];
                  return [<Row key={it.id} label={it.label} value={formatAnswer(it, v as FormValue, names) || '–'} />];
                }
                if (it.repeat) {
                  const rows = (ev.active[it.id] as FormRowValue[] | undefined) ?? [];
                  return [
                    <Row
                      key={it.id}
                      label={it.title}
                      value={
                        rows.length
                          ? rows.map((r, i) => `${it.repeat!.itemLabel || 'Eintrag'} ${i + 1}: ${it.items.filter((q) => q.type !== 'info' && r[q.id] != null).map((q) => `${q.label} ${formatAnswer(q, r[q.id], names)}`).join(' · ')}`).join('\n')
                          : '–'
                      }
                    />,
                  ];
                }
                return it.items
                  .filter((q) => ev.visible.has(q.id) && q.type !== 'info')
                  .map((q) => <Row key={q.id} label={q.label} value={formatAnswer(q, (q.type === 'calc' ? ev.calc[q.id]?.value : ev.active[q.id]) as FormValue, names) || '–'} />);
              })}
            </dl>
          </section>
        ))}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-0.5 px-3 py-2 sm:grid-cols-[minmax(0,14rem)_1fr] sm:gap-3">
      <dt className="text-[var(--ink-soft)]">{label}</dt>
      <dd className="whitespace-pre-line [overflow-wrap:anywhere]">{value}</dd>
    </div>
  );
}
